import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import type { ChangeInputHandler } from '../../../types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { formatMoney } from 'app/lib/formatters';

import { ChangeEntryAmount, changeEntryAmount } from '../../../actions/entries';
import useEntryDisabledTitles from '../../../hooks/useEntryDisabledTitles';
import type { RecordOfEntryAmountType } from '../../../records';
import { selectSingleBetsInfo } from '../../../selectors/stakeTypes/single';
import type { PledgeStakeDAO } from '../../../types/backend/paymentOptions';
import AmountInput from '../../AmountInputByAccountType';

import dictionary from './AmountInput.dictionary.json';

// ---

type StoreProps = {
  pledgeStake?: PledgeStakeDAO | null;
  freeBet?: FreeBet | null;
  amount?: RecordOfEntryAmountType | null;
  minValue: number;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeEntryAmount>;
};

type EntryAmountInputProps = {
  betId: number;
  hasError: boolean;
};

const EntryAmountInput: FC<EntryAmountInputProps & DispatchProp & StoreProps> = ({
  betId,
  hasError,
  amount,
  minValue,
  pledgeStake,
  freeBet,
  dispatch,
}) => {
  const { lang, t } = useTranslation();
  const disabledTitles = useEntryDisabledTitles(betId);

  const changeAmountInputHandler: ChangeInputHandler = useCallback(({ value, account }) => {
    if (!account) {
      console.error('EntryAmountInput -> changeAmountInputHandler -> `account` not found');
      return;
    }

    dispatch(changeEntryAmount(betId, value, account));
  }, [betId]);

  const minValuePlaceholder = useMemo(() => {
    if (minValue > 0) {
      return formatMoney(minValue, lang);
    }

    return 0;
  }, [lang, minValue]);

  if (!amount) {
    return null;
  }

  const placeholder = minValue > 0
    ? t(dictionary.minValuePlaceholder, { minValuePlaceholder })
    : t(dictionary.amountPlaceholder);

  return (
    <AmountInput betId={betId}
                 name={`bet_${betId}_amount`}
                 error={hasError}
                 placeholder={placeholder}
                 account={amount.account}
                 value={amount.value}
                 pledgeStake={pledgeStake}
                 freeBet={freeBet}
                 minValue={minValue}
                 disabledTitle={disabledTitles}
                 onChange={changeAmountInputHandler} />
  );
};

const mapStateToProps = (state: ReduxState, { betId }: EntryAmountInputProps): StoreProps => {
  const betInfo = selectSingleBetsInfo(state).get(betId);

  const amount = betInfo?.amount;
  const pledgeStake = betInfo?.pledgeStake;
  const freeBet = betInfo?.freeBet;
  const amountSelector = amount?.selector;

  return {
    pledgeStake,
    freeBet,
    amount: amountSelector ? amountSelector(state) : amount,
    minValue: betInfo?.limits.min || 0,
  };
};

export default connect(mapStateToProps)(EntryAmountInput);
